import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Activity, Stethoscope } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { BookButton } from "@/components/site/BookButton";
import { Seo } from "@/components/site/Seo";
import { ConnectionDiagram } from "@/components/chain/ConnectionDiagram";

export default function ConditionDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [services, setServices] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setC(null);
    api.get(`/concerns/${slug}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) return <div className="pt-48 text-center pb-40"><h1 className="font-serif text-4xl">Not found</h1><Link to="/conditions" className="link-underline mt-4 inline-block">Back</Link></div>;
  if (!c) return <div className="h-screen" />;

  const related = services.filter((s) => (c.related_services || []).includes(s.slug));

  return (
    <div>
      <Seo title={`${c.name} in Gardner, KS | Psalm 30:2 Chiropractic`} description={c.summary} />
      <section className="pt-36 lg:pt-44 pb-14 px-6 md:px-10 lg:px-14">
        <div className="max-w-[1500px] mx-auto">
          <Link to="/conditions" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-taupe hover:text-espresso mb-10" data-testid="condition-back">
            <ArrowLeft size={15} /> Where Do You Need Relief?
          </Link>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="overline mb-5">Concern</div>
              <h1 className="font-serif font-light text-5xl lg:text-7xl leading-[1.0] text-espresso">{c.name}</h1>
              <p className="mt-8 text-xl font-light leading-relaxed text-espressosoft/85">{c.detail}</p>
            </div>
            <Reveal delay={120} className="lg:col-span-6">
              <div className="overflow-hidden rounded-sm aspect-[4/3] lg:ml-auto">
                <img src={resolveImg(c.image)} alt={c.name} className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {c.biomechanics && (
        <section className="bg-ivory pt-6 pb-16 lg:pb-24 px-6 md:px-10 lg:px-14" data-testid="condition-biomechanics">
          <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <div className="overline mb-5 flex items-center gap-2"><Activity size={15} className="text-brass" /> How the body connects</div>
              <h2 className="font-serif font-light text-3xl lg:text-4xl text-espresso leading-snug mb-6">The chain behind {c.name.toLowerCase()}</h2>
              <p className="text-lg font-light leading-relaxed text-espressosoft/85">{c.biomechanics}</p>
              <p className="mt-6 text-base font-light leading-relaxed text-espressosoft/70">It all runs through one system: your nervous system reads the chain of fascia, muscles and ligaments and decides how you move, brace and feel pain. Change the input, and the output changes.</p>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-espresso text-ivory rounded-sm p-8 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="grid place-items-center h-11 w-11 rounded-xl bg-brass/20"><Stethoscope size={20} className="text-brass" /></div>
                  <div className="overline !text-brass">We'll assess in office</div>
                </div>
                <p className="text-ivory/80 font-light leading-relaxed">We don't guess. In your visit we test movement, strength and how your nervous system is loading the whole chain — foot to skull — to uncover the weak areas and determine the true root of the issue. Then we build your plan around exactly what we find.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      <ConnectionDiagram slug={c.slug} name={c.name} />

      {related.length > 0 && (
        <section className="bg-stone py-20 lg:py-28 px-6 md:px-10 lg:px-14" data-testid="condition-related">
          <div className="max-w-[1500px] mx-auto">
            <Reveal><div className="overline mb-4">Relevant options to explore</div>
              <h2 className="font-serif font-light text-3xl lg:text-4xl text-espresso mb-3">Assessment & treatment paths</h2>
              <p className="text-sm text-taupe max-w-xl mb-12">These connections are starting points for discussion — not guaranteed diagnoses. Your plan is always built from an individual assessment.</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((s, i) => (
                <Reveal key={s.slug} delay={(i % 3) * 60}>
                  <Link to={`/treatments/${s.slug}`} className="group block bg-ivory border border-espresso/10 rounded-sm p-8 hover:-translate-y-1 transition-transform duration-500" data-testid={`related-service-${s.slug}`}>
                    <div className="overline mb-2">{s.category}</div>
                    <h3 className="font-serif text-2xl text-espresso group-hover:text-taupe transition-colors flex items-center gap-2">{s.name}</h3>
                    <p className="mt-2 text-espressosoft/70 font-light text-sm">{s.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-espresso">Learn more <ArrowRight size={13} /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-ivory py-20 px-6 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-4xl lg:text-5xl text-espresso mb-6">Let's understand what's going on.</h2>
          <BookButton label="Book Your Visit" variant="primary" track_label={`condition-${c.slug}`} testid="condition-book-button" />
        </Reveal>
      </section>
    </div>
  );
}
